import React, { useState } from 'react';
import propTypes from 'prop-types';
import { getMessage } from 'util/locale';

import ValueWithUnit from 'common/ValueWithUnit';
import { convertFertilizerSummaryForLocale } from 'util/unitConversion';
import ScriptProductApplicationSummaryStats from 'edit/ScriptProductApplicationSummaryStats';
import ValueWithHeader from 'common/ValueWithHeader';
import { scriptTypes } from 'constants/scripts';
import { NITROGEN_APPLICATION_RATE_TYPES } from 'constants/values';
import ScriptFertilizerApplicationModal from './ScriptFertilizerApplicationModalConnect';

const baseString = 'vra-script-product-application-summary';
const ScriptFertilizerApplicationSummary = ({ // NOSONAR False-Positive Case statement
  numPopulatedZones,
  numZones,
  script,
  scriptType,
  scriptTypeIsNitrogen,
  summary,
  isCustom,
  nitrogenApplicationRateType,
  showEditAllButton
}) => {
  const [showEditAllModal, setShowEditAllModal] = useState(false); // consider moving this to redux

  const {
    convertedFertilizerRate,
    convertedTotalProduct,
    convertedAvgNitrogenRate
  } = convertFertilizerSummaryForLocale(summary);

  let content = (
    <p>{getMessage('placeholder-text__script-product-application-summary')}</p>
  );

  if (numZones > 0) {
    const statsContent = [{ // common to both fertility script types
      header: scriptType === scriptTypes.FERTILITY
        ? getMessage('title-text__avg-fertilizer-rate')
        : getMessage('title-text__protection__avg-fertilizer-rate'),
      value: (
        <ValueWithUnit
          editable={showEditAllButton
              && scriptTypeIsNitrogen
              && !isCustom 
              && nitrogenApplicationRateType === NITROGEN_APPLICATION_RATE_TYPES.fertilizer
          }
          onEdit={() => setShowEditAllModal(true)}
          unit={convertedFertilizerRate ? convertedFertilizerRate.symbol.quantitySpecific : 0}
          value={convertedFertilizerRate ? convertedFertilizerRate.scalarLocaleString : undefined}
        />
      )}, {
      header: scriptType === scriptTypes.FERTILITY
        ? getMessage('title-text__total-product')
        : getMessage('title-text__protection__total-product'),
      value: (
        <ValueWithUnit
          unit={convertedTotalProduct ? convertedTotalProduct.symbol.quantitySpecific : 0}
          value={convertedTotalProduct ? convertedTotalProduct.scalarLocaleString : undefined}/>
      )}
    ];

    if (scriptTypeIsNitrogen && !isCustom) {
      // put the nitrogen rate at the beginning
      statsContent.unshift({
        header: getMessage('title-text__avg-nitrogen-rate'),
        value: (
          <ValueWithUnit
            editable={
              showEditAllButton 
              && nitrogenApplicationRateType === NITROGEN_APPLICATION_RATE_TYPES.nitrogen
            }
            onEdit={() => setShowEditAllModal(true)}
            unit={convertedAvgNitrogenRate.symbol.quantitySpecific}
            value={convertedAvgNitrogenRate.scalarLocaleString}
          />
        )});
    }

    content = (
      <ScriptProductApplicationSummaryStats>
        {
          statsContent.map((stat, index) => {
            return (
              <ValueWithHeader header={stat.header} key={`summary-stat-${index}`} value={stat.value} />
            );
          })
        }
      </ScriptProductApplicationSummaryStats>
    );
  }

  return (
    <section className={baseString} data-test-id="vra-script-fertilizer-application-summary">
      <div className={`${baseString}__header-container`}>
        <header>{getMessage('title-text__script-creator--summary')}</header>
        {
          numZones > 0
            ? <span data-test-id='num-of-zones'>{getMessage('text__zone-summary__num-entered--num-and-zones',
              { numPopZones: numPopulatedZones, numZones: numZones })}
            </span> : null
        }
      </div>
      <div>
        {content}
      </div>
      {showEditAllModal && (
        <ScriptFertilizerApplicationModal 
          fertilizerRate={convertedFertilizerRate}
          nitrogenApplicationRateType={nitrogenApplicationRateType}
          nitrogenRate={convertedAvgNitrogenRate}
          onCancel={() => setShowEditAllModal(false)}
          onSave={() => setShowEditAllModal(false)}
          script={script}
        />
      )}
    </section>
  );
};
ScriptFertilizerApplicationSummary.propTypes = {
  isCustom: propTypes.bool,
  nitrogenApplicationRateType: propTypes.string,
  numPopulatedZones: propTypes.number.isRequired,
  numZones: propTypes.number.isRequired,
  script: propTypes.object,
  scriptType: propTypes.string,
  scriptTypeIsNitrogen: propTypes.bool.isRequired,
  showEditAllButton: propTypes.bool,
  summary: propTypes.object.isRequired
};

export default ScriptFertilizerApplicationSummary;
